import { Workbook } from 'exceljs';
import Decimal from "decimal.js";
import { CellFormat } from "@/lib/excel";
import { TicketGroupBuilder } from './TicketGroupBuilder';

export function createGradeFactorWorkbook(ticketBuilder: TicketGroupBuilder, currentMonth: string, previousMonth: string): Workbook {
  // Create new workbook for the report
  const reportWorkbook = new Workbook();

  // Create Summary worksheet
  const summarySheet = reportWorkbook.addWorksheet('Summary');
  summarySheet.columns = [
    { header: 'Commodity', key: 'commodity', width: 15 },
    { header: 'Total Tickets', key: 'totalTickets', width: 15 },
    { header: 'Net Discount', key: 'netDiscount', width: 15, style: { numFmt: CellFormat.ACCOUNTING } }
  ];

  let totalNetDiscount = 0;

  // Process each commodity
  for (const [commodity, tickets] of Object.entries(ticketBuilder.ticketsByCommodity)) {
    // Calculate summary data
    const totalTickets = Object.keys(tickets).length;
    const netDiscount = Object.values(tickets).reduce((sum, ticket) => {
      return sum + ticket.entries.reduce((ticketSum, entry) => ticketSum + entry.reportedDiscount, 0);
    }, 0);

    totalNetDiscount += netDiscount;

    // Add to summary sheet
    summarySheet.addRow({
      commodity,
      totalTickets,
      netDiscount
    });

    // Create commodity-specific worksheet
    const commoditySheet = reportWorkbook.addWorksheet(commodity);
    commoditySheet.columns = [
      { header: 'Customer', key: 'customer', width: 30 },
      { header: 'Ticket Number', key: 'ticketNumber', width: 15, style: { numFmt: '@' } },
      { header: 'Net Discount', key: 'netDiscount', width: 15, style: { numFmt: CellFormat.ACCOUNTING } },
      { header: 'Purchase/Sales', key: 'purchaseOrSales', width: 15 },
      { header: 'Documents', key: 'documents', width: 20 }
    ];

    // Add ticket details
    for (const ticket of Object.values(tickets)) {
      const netDiscount = ticket.entries.reduce((sum, entry) => sum.plus(entry.reportedDiscount), new Decimal(0)).toNumber();

      // Get the first entry for customer name
      const firstEntry = ticket.entries[0];

      // Get unique values for the last columns
      const uniquePurchaseSales = [...new Set(ticket.entries.map(e => e.purchaseOrSales))];

      // Create unique document strings combining type and version
      const uniqueDocuments = [...new Set(ticket.entries.map(e => {
        const version = e.documentVersion === 'currentMonth' ? currentMonth :
                       e.documentVersion === 'previousMonth' ? previousMonth :
                       e.documentVersion;
        return e.document === 'gfCharges' ? e.document : `${e.document} (${version})`;
      }))];

      commoditySheet.addRow({
        customer: firstEntry.fullName,
        ticketNumber: ticket.ticketNumber,
        netDiscount,
        purchaseOrSales: uniquePurchaseSales.join(', '),
        documents: uniqueDocuments.join(', ')
      });
    }

    // Freeze the first row
    commoditySheet.views = [
      { state: 'frozen', xSplit: 0, ySplit: 1 }
    ];

    // Add filters to all columns
    const lastRow = commoditySheet.rowCount;
    commoditySheet.autoFilter = {
      from: 'A1',
      to: { row: lastRow, column: 5 }
    };
  }

  // Add blank row
  summarySheet.addRow({});

  // Add total row
  const totalRow = summarySheet.addRow({
    commodity: 'Total',
    totalTickets: '',
    netDiscount: totalNetDiscount
  });
  totalRow.font = { bold: true };

  return reportWorkbook;
}
